import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Trophy, Star, Home, RotateCcw, Award } from 'lucide-react';
import { useGame } from '../context/GameContext';
import { useBadges } from '../hooks/useBadges';
import BadgeNotification from '../components/BadgeNotification';

const ResultScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeGame, playerId, selectedAge, selectedSubject } = useGame();
  const [showConfetti, setShowConfetti] = useState(true);
  const gameScore = location.state?.score || 0;
  const totalQuestions = location.state?.totalQuestions || 0;
  const correctAnswers = location.state?.correctAnswers || 0;
  const gameType = location.state?.gameType || 'quiz';

  // Badge system
  const { 
    recordGameCompletion, 
    currentBadge, 
    showNotification, 
    handleBadgeClose,
    totalNewBadges,
    currentBadgeNumber
  } = useBadges(playerId);

  const percentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
  const isPerfect = percentage === 100;

  useEffect(() => {
    completeGame();
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    
    // Record game completion for badge tracking
    if (playerId) {
      recordGameCompletion(gameType, gameScore, isPerfect, selectedSubject, selectedAge);
    }
    
    return () => clearTimeout(timer);
  }, []);

  const getMessage = () => {
    if (percentage >= 90) return { text: 'Amazing! You\'re a superstar!', color: 'from-yellow-400 to-orange-400' };
    if (percentage >= 70) return { text: 'Great job! Keep it up!', color: 'from-green-400 to-emerald-400' };
    if (percentage >= 50) return { text: 'Good try! Practice more!', color: 'from-blue-400 to-cyan-400' };
    return { text: 'Nice effort! Try again!', color: 'from-purple-400 to-pink-400' };
  };

  const message = getMessage();

  return (
    <div className="min-h-screen p-4 md:p-8 flex items-center justify-center" style={{
      background: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)'
    }}>
      {/* Badge Notification */}
      {showNotification && currentBadge && (
        <BadgeNotification 
          badge={currentBadge} 
          onClose={handleBadgeClose}
          autoClose={true}
        />
      )}

      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="confetti-particle"
              style={{
                left: `${Math.random() * 100}%`,
                background: `hsl(${Math.random() * 360}, 70%, 60%)`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
      )}

      <div className="max-w-2xl w-full">
        <Card className="p-8 md:p-12 bg-white/95 backdrop-blur-sm shadow-2xl bounce-in" style={{ borderRadius: '32px' }}>
          <div className="text-center">
            <div className={`w-32 h-32 mx-auto mb-6 bg-gradient-to-br ${message.color} rounded-full flex items-center justify-center pulse-animation`}>
              <Trophy className="w-20 h-20 text-white" />
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-4">Game Complete!</h1>
            <p className="text-3xl font-bold mb-8" style={{ color: `hsl(${percentage * 1.2}, 70%, 50%)` }}>
              {message.text}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card className="p-6 bg-gradient-to-br from-yellow-100 to-yellow-200" style={{ borderRadius: '20px' }}>
                <Star className="w-12 h-12 mx-auto mb-2 text-yellow-600" />
                <p className="text-sm text-gray-600">Score</p>
                <p className="text-4xl font-bold text-yellow-700">{gameScore}</p>
              </Card>
              <Card className="p-6 bg-gradient-to-br from-green-100 to-green-200" style={{ borderRadius: '20px' }}>
                <Trophy className="w-12 h-12 mx-auto mb-2 text-green-600" />
                <p className="text-sm text-gray-600">Correct</p>
                <p className="text-4xl font-bold text-green-700">{correctAnswers}/{totalQuestions}</p>
              </Card>
              <Card className="p-6 bg-gradient-to-br from-blue-100 to-blue-200" style={{ borderRadius: '20px' }}>
                <Star className="w-12 h-12 mx-auto mb-2 text-blue-600" />
                <p className="text-sm text-gray-600">Percentage</p>
                <p className="text-4xl font-bold text-blue-700">{percentage}%</p>
              </Card>
            </div>

            <div className="space-y-4">
              <Button
                onClick={() => navigate('/badges')}
                className="w-full py-6 text-xl font-bold transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  borderRadius: '16px'
                }}
              >
                <Award className="w-6 h-6 mr-2" />
                View Badges
              </Button>
              <Button
                onClick={() => navigate('/age-selection')}
                className="w-full py-6 text-xl font-bold transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '16px'
                }}
              >
                <RotateCcw className="w-6 h-6 mr-2" />
                Play Again
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full py-6 text-xl font-bold border-4 transition-all duration-300 hover:scale-105"
                style={{ borderRadius: '16px' }}
              >
                <Home className="w-6 h-6 mr-2" />
                Go Home
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ResultScreen;
